import { useState } from "react";
import RichText from "@/components/RichText";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";

const DEFAULTS = {
  eyebrow: "",
  heading: "Nous trouver",
  subheading: "",
  address: "",
  map_query: "",
  phone: "",
  email: "",
  hours: "",
  cta_label: "Itinéraire",
  cta_url: "",
  variant: "light",
  anchor_id: "",
};

const strip = (v) => (v || "").replace(/<[^>]+>/g, "").trim();
const mapEmbed = (q) => `https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed`;
const directionsUrl = (q) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;

function InfoLines({ store, dark }) {
  const lineCls = dark ? "text-white/80" : "text-graphite";
  const iconCls = "h-5 w-5 text-gold shrink-0 mt-0.5";
  return (
    <div className="space-y-4">
      {store.address && (
        <div className="flex gap-3"><MapPin className={iconCls} /><p className={`text-[15px] leading-relaxed whitespace-pre-line ${lineCls}`}>{store.address}</p></div>
      )}
      {store.hours && (
        <div className="flex gap-3"><Clock className={iconCls} /><p className={`text-[15px] leading-relaxed whitespace-pre-line ${lineCls}`}>{store.hours}</p></div>
      )}
      {store.phone && (
        <div className="flex gap-3 items-start"><Phone className={iconCls} /><a href={`tel:${store.phone.replace(/\s+/g, "")}`} className={`text-[15px] hover:text-gold transition-colors ${lineCls}`}>{store.phone}</a></div>
      )}
      {store.email && (
        <div className="flex gap-3 items-start"><Mail className={iconCls} /><a href={`mailto:${store.email}`} className={`text-[15px] hover:text-gold transition-colors break-all ${lineCls}`}>{store.email}</a></div>
      )}
    </div>
  );
}

export default function MapLocationSection({ props = {}, blocks }) {
  const t = { ...DEFAULTS, ...props };
  const dark = t.variant === "dark";

  // Mode multi-boutiques dès qu'il y a des blocs "location" ; sinon on
  // retombe sur l'adresse unique définie au niveau de la section.
  const storeBlocks = (blocks || []).filter((b) => b.enabled !== false);
  const stores = storeBlocks.length
    ? storeBlocks.map((b) => ({
        key: b.id,
        title: strip(b.props?.title),
        address: strip(b.props?.address),
        query: strip(b.props?.map_query) || strip(b.props?.address),
        phone: strip(b.props?.phone),
        email: strip(b.props?.email),
        hours: strip(b.props?.hours),
        cta_url: strip(b.props?.cta_url),
      }))
    : [{
        key: "single",
        title: "",
        address: t.address,
        query: (t.map_query || t.address || "").trim(),
        phone: t.phone,
        email: t.email,
        hours: t.hours,
        cta_url: (t.cta_url || "").trim(),
      }];

  const [active, setActive] = useState(0);
  const multi = stores.length > 1;
  const current = stores[Math.min(active, stores.length - 1)];
  const query = current.query;
  const directions = current.cta_url || (query ? directionsUrl(query) : "");

  if (!t.heading && !query && !current.phone && !current.email) return null;

  const card = dark ? "bg-white/5 border-white/10" : "bg-white border-stone";

  return (
    <section id={t.anchor_id || undefined} data-testid="map-location-section" className={`scroll-mt-28 ${dark ? "bg-ink" : ""}`}>
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-28">
        {(t.eyebrow || t.heading || t.subheading) && (
          <div className="max-w-2xl mb-12">
            {t.eyebrow && (
              <RichText as="span" className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold tracking-wide uppercase mb-4 ${dark ? "border-white/15 bg-white/5 text-gold" : "border-stone bg-sand text-gold"}`} html={t.eyebrow} />
            )}
            {t.heading && (
              <RichText as="h2" className={`font-display font-semibold text-[30px] md:text-[34px] leading-tight tracking-[-0.02em] ${dark ? "text-white" : "text-ink"}`} html={t.heading} />
            )}
            {t.subheading && (
              <RichText as="p" preserveParagraphs className={`text-[16px] mt-4 ${dark ? "text-white/60" : "text-graphite"}`} html={t.subheading} />
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-stretch">
          <div className={`rounded-2xl border p-7 md:p-9 flex flex-col ${card}`}>
            {multi ? (
              <>
                <div className="space-y-2 mb-6" data-testid="map-store-list">
                  {stores.map((s, i) => {
                    const selected = i === active;
                    return (
                      <button
                        type="button"
                        key={s.key}
                        data-testid={`map-store-${i}`}
                        onClick={() => setActive(i)}
                        className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${selected ? "border-gold bg-gold/10" : dark ? "border-white/10 hover:border-white/25" : "border-stone hover:border-graphite"}`}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className={`h-4 w-4 shrink-0 ${selected ? "text-gold" : dark ? "text-white/50" : "text-ash"}`} />
                          <span className={`font-medium text-[15px] ${dark ? "text-white" : "text-ink"}`}>{s.title || s.address || `Boutique ${i + 1}`}</span>
                        </div>
                        {s.title && s.address && (
                          <div className={`text-[13px] mt-0.5 pl-6 whitespace-pre-line ${dark ? "text-white/50" : "text-ash"}`}>{s.address}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="flex-1">
                  <InfoLines store={current} dark={dark} />
                </div>
              </>
            ) : (
              <div className="flex-1">
                <InfoLines store={current} dark={dark} />
              </div>
            )}
            {directions && (
              <a
                href={directions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-directions-btn"
                className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-white font-semibold text-[15px] transition-transform hover:-translate-y-0.5 w-fit"
              >
                <Navigation className="h-4 w-4" />
                {t.cta_label || "Itinéraire"}
              </a>
            )}
          </div>

          <div className="rounded-2xl overflow-hidden border border-stone min-h-[340px] bg-sand">
            {query ? (
              <iframe
                key={query}
                data-testid="map-iframe"
                title={`Carte — ${current.title || current.address || "localisation"}`}
                src={mapEmbed(query)}
                className="w-full h-full min-h-[340px]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full min-h-[340px] flex items-center justify-center text-ash text-[14px] px-6 text-center">
                Renseignez l'adresse pour afficher le plan.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
